import { useEffect } from 'react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import prose from '@/styles/prose.module.css';
import LegalDoc, { LegalSection as Section } from '@/components/legal/LegalDoc';

const Paragraph = ({ children }) => (
  <p className={prose.paragraph}>{children}</p>
);

const BulletList = ({ items }) => (
  <ul className={prose.list}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export default function TermsOfService() {
  usePageMeta(PAGE_META.terms);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="terms-of-service-page">
      <LandingHeader />

      <main>

      <LegalDoc
        title="Terms of Service"
        updated="August 13, 2026"
        isoDate="2026-08-13"
        readingTime="3 min read"
        testId="terms-document"
      >

        <Section title="1. Acceptance of Terms">
          <Paragraph>
            By accessing or using the MyCaseLoan.com platform (&quot;Service&quot;), operated by My Case Loan, LLC, a Florida limited liability company, you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, you may not access or use the Service.
          </Paragraph>
          <Paragraph>
            These Terms apply to all users of the Service, including Clients/Plaintiffs, Lawyers, and Funders.
          </Paragraph>
        </Section>

        <Section title="2. Description of Service">
          <Paragraph>MyCaseLoan.com provides a platform that connects plaintiffs seeking pre-settlement funding with lawyers and funding companies. Our Service facilitates:</Paragraph>
          <BulletList items={[
            'Submission and management of funding applications',
            'Secure document upload and sharing',
            'Communication between parties involved in pre-settlement funding',
            'Tracking of funding requests and disbursements',
          ]} />
          <Paragraph>MyCaseLoan.com is not a lender and does not provide legal advice. We serve solely as a platform to connect parties.</Paragraph>
        </Section>

        <Section title="3. User Accounts">
          <Paragraph>To use certain features of the Service, you must register for an account. You agree to:</Paragraph>
          <BulletList items={[
            'Provide accurate, current, and complete information during registration',
            'Maintain the security of your password and account',
            'Notify us immediately of any unauthorized use of your account',
            'Accept responsibility for all activities that occur under your account',
          ]} />
        </Section>

        <Section title="4. Communications">
          <Paragraph>
            By providing your email address or telephone number, you agree that we may contact you about your account and about any application you begin on the Service, including an application you start but do not complete.
          </Paragraph>
          <Paragraph>
            You may opt out of non-essential messages at any time using the unsubscribe link in any email, or by contacting us at the address below. Messages about your account, your application, or a funding offer are transactional and will continue while your application is active. We will only send you text messages if you have asked us to.
          </Paragraph>
        </Section>

        <Section title="5. User Responsibilities">
          <h3 className={prose.subhead}>5.1 For Clients/Plaintiffs</h3>
          <Paragraph>
            You represent that all information provided in your funding application is truthful and accurate. You understand that providing false information may result in termination of your account and potential legal consequences.
          </Paragraph>

          <h3 className={prose.subhead}>5.2 For Lawyers</h3>
          <Paragraph>
            You represent that you are a licensed attorney in good standing. You agree to verify client information accurately and upload only authentic case documents.
          </Paragraph>

          <h3 className={prose.subhead}>5.3 For Funders</h3>
          <Paragraph>
            You represent that you are authorized to provide pre-settlement funding. You agree to maintain confidentiality of all case information accessed through the platform.
          </Paragraph>
        </Section>

        <Section title="6. Confidentiality">
          <Paragraph>
            All parties agree to maintain strict confidentiality of information shared through the platform. Case details, financial information, and personal data must not be disclosed to unauthorized parties. Violation of confidentiality may result in immediate account termination and potential legal action.
          </Paragraph>
        </Section>

        <Section title="7. Intellectual Property">
          <Paragraph>
            The Service and its original content, features, and functionality are owned by My Case Loan, LLC and are protected by international copyright, trademark, and other intellectual property laws.
          </Paragraph>
        </Section>

        <Section title="8. Limitation of Liability">
          <Paragraph>
            My Case Loan, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service. We do not guarantee any specific outcomes from funding applications or transactions facilitated through the platform.
          </Paragraph>
        </Section>

        <Section title="9. Termination">
          <Paragraph>
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms.
          </Paragraph>
        </Section>

        <Section title="10. Changes to Terms">
          <Paragraph>
            We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on this page and updating the &quot;Last updated&quot; date. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
          </Paragraph>
        </Section>

        <Section title="11. Contact Us">
          <Paragraph>If you have questions about these Terms, please contact us at:</Paragraph>
          <p className={prose.address}>
            My Case Loan, LLC<br />
            A Florida Limited Liability Company<br />
            Email: legal@mycaseloan.com
          </p>
        </Section>

      </LegalDoc>
      </main>


      <LandingFooter />
    </div>
  );
}
